import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BookText, Cloud, Loader2, MonitorSmartphone, ShieldCheck, Ticket, Users } from 'lucide-react';
import { useDeviceOrbit, type DeviceOrbit } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { ExtLink, Field, Panel, Pill, TicketList, type Tone } from '@/components/insights/RecordKit';
import { HuntressDeviceCard } from '@/components/soc/HuntressCards';
import { ScreenConnectDeviceCard } from '@/components/rmm/ScreenConnectCards';
import { avStatusMeta, fmtAge, patchStatusMeta } from '@/lib/rmm-helpers';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, cippTenantPath, companyPath, docsOrgPath, insightsPersonPath, rmmDevicePath } from '@/lib/routes';

/**
 * Device Orbit (`/insights/devices/:key`) — one machine across Datto RMM, Intune and IT Glue, how
 * Cosmos matched them, check-in drift and flags, open alerts, the Autotask CI's open tickets and
 * the people tied to it. Reached from the header Find box.
 */

const STATE: Record<NonNullable<DeviceOrbit['state']>, { label: string; tone: Tone; note: string }> = {
  'in-sync': { label: 'In sync', tone: 'good', note: 'In Datto RMM and Intune, checking in to both.' },
  drift: { label: 'Check-in drift', tone: 'warn', note: 'In both, but their last check-ins disagree.' },
  'rmm-only': { label: 'Not in Intune', tone: 'warn', note: 'This client uses Intune, but this device isn\'t enrolled.' },
  'intune-only': { label: 'No RMM agent', tone: 'bad', note: 'In Intune, but Datto RMM has no agent for it.' },
  single: { label: 'Single source', tone: 'muted', note: 'Only one product applies to this device or client.' },
};

const COMPLIANCE_TONE = (c: string | null): Tone => (c === 'compliant' ? 'good' : c === 'noncompliant' ? 'bad' : c === 'inGracePeriod' ? 'warn' : 'muted');

export default function DeviceOrbitPage() {
  const key = useParams<{ key: string }>().key;
  const { data: d, isLoading, error, isFetching } = useDeviceOrbit(key);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
      <Link to={ROUTES.insights.find} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Find
      </Link>
      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Gathering this device from every module…</div>
      ) : error ? (
        <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
      ) : d ? (
        <>
          <PageHeader title={d.hostname} isFetching={isFetching}
            subtitle={<>
              {d.companyId != null ? <Link to={companyPath(d.companyId)} className="hover:underline">{d.companyName}</Link> : d.companyName ?? 'No company'}
              {d.os && <> · {d.os}</>}{d.serial && <> · SN {d.serial}</>}
            </>} />

          {/* Identity: how the sources were joined. */}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            {d.state && <Pill tone={STATE[d.state].tone}>{STATE[d.state].label}</Pill>}
            <span className="text-muted-foreground">{d.state ? STATE[d.state].note : 'Only IT Glue documents this device.'}</span>
            {d.match && <span className="text-muted-foreground">Matched by <b className="font-medium text-foreground">{d.match.by}</b> ({d.match.confidence} confidence).</span>}
            {d.driftHours != null && d.driftHours > 0 && <span className="text-muted-foreground">Check-ins {d.driftHours} h apart (limit {d.driftLimit} h).</span>}
          </div>
          {d.flags.length > 0 && (
            <div className="mt-2 space-y-1">
              {d.flags.map((f) => <div key={f} className="flex items-center gap-1.5 text-sm text-warning"><AlertTriangle className="h-3.5 w-3.5" />{f}</div>)}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-4">
            <Panel title="Datto RMM" icon={MonitorSmartphone} muted={!d.rmm}
              actions={d.rmm && <><Link to={rmmDevicePath(d.rmm.uid)} className="text-muted-foreground hover:text-foreground hover:underline">Device page →</Link><ExtLink href={d.rmm.portalUrl}>Datto</ExtLink></>}>
              {!d.rmm ? <Empty>No Datto RMM agent{d.state === 'intune-only' ? ' (a gap: this client uses Datto RMM)' : ''}.</Empty> : <div className="py-1">
                <Field label="Status">{d.rmm.online ? <Pill tone="good">online</Pill> : <Pill tone={d.rmm.stale ? 'bad' : 'warn'}>offline {fmtAge(d.rmm.lastSeen)}{d.rmm.stale ? ' · stale' : ''}</Pill>}</Field>
                <Field label="Site">{d.rmm.siteName}</Field>
                <Field label="Last user">{d.rmm.lastLoggedInUser}</Field>
                <Field label="Hardware">{[d.rmm.manufacturer, d.rmm.model].filter(Boolean).join(' ') || null}</Field>
                <Field label="IP">{[d.rmm.intIp, d.rmm.extIp].filter(Boolean).join(' · ') || null}</Field>
                <Field label="Patching">{patchStatusMeta(d.rmm.patchStatus)?.label}</Field>
                <Field label="Antivirus">{d.rmm.antivirusProduct ? `${d.rmm.antivirusProduct} · ${avStatusMeta(d.rmm.antivirusStatus)?.label ?? '?'}` : null}</Field>
                <Field label="Reboot">{d.rmm.rebootRequired ? <Pill tone="warn">required</Pill> : d.rmm.lastReboot ? `${fmtAge(d.rmm.lastReboot)} ago` : null}</Field>
                <Field label="Warranty">{d.rmm.warrantyDate}</Field>
                <Field label="Agent">{d.rmm.agentVersion}</Field>
              </div>}
            </Panel>

            <Panel title="Intune" icon={Cloud} muted={!d.intune}
              actions={d.intune && <Link to={cippTenantPath(d.intune.tenant.customerId)} className="text-muted-foreground hover:text-foreground hover:underline">{d.intune.tenant.displayName} →</Link>}>
              {!d.intune ? <Empty>Not in Intune{d.state === 'rmm-only' ? ' (a gap: this client uses Intune)' : ''}.</Empty> : <div className="py-1">
                <Field label="Compliance"><Pill tone={COMPLIANCE_TONE(d.intune.compliance)}>{d.intune.compliance ?? 'unknown'}</Pill></Field>
                <Field label="Last sync">{d.intune.lastSync ? `${fmtAge(d.intune.lastSync)} ago` : null}</Field>
                <Field label="Primary user">{d.intune.user ? <Link to={insightsPersonPath(d.intune.user.toLowerCase())} className="hover:underline">{d.intune.userName || d.intune.user}</Link> : null}</Field>
                <Field label="Name">{d.intune.deviceName}</Field>
                <Field label="Hardware">{[d.intune.manufacturer, d.intune.model].filter(Boolean).join(' ') || null}</Field>
                <Field label="OS">{d.intune.os}</Field>
                <Field label="Encrypted">{d.intune.encrypted == null ? null : d.intune.encrypted ? 'Yes' : <Pill tone="bad">No</Pill>}</Field>
                <Field label="Autopilot">{d.intune.autopilot ? 'Yes' : 'No'}</Field>
                <Field label="Ownership">{d.intune.ownership}</Field>
                <Field label="Enrolled">{d.intune.enrolled ? fmtRelative(d.intune.enrolled) : null}</Field>
              </div>}
            </Panel>

            <Panel title="IT Glue" icon={BookText} muted={!d.itglue}
              actions={d.itglue && <><Link to={docsOrgPath(d.itglue.orgId, 'configurations')} className="text-muted-foreground hover:text-foreground hover:underline">Org →</Link>{d.itglue.url && <ExtLink href={d.itglue.url}>IT Glue</ExtLink>}</>}>
              {!d.itglue ? <Empty>No IT Glue configuration for this device.</Empty> : <div className="py-1">
                <Field label="Name">{d.itglue.name}</Field>
                <Field label="Type">{d.itglue.type}</Field>
                <Field label="Status">{d.itglue.status}</Field>
                <Field label="Contact">{d.itglue.contact}</Field>
                <Field label="Location">{d.itglue.location}</Field>
                <Field label="Warranty">{d.itglue.warrantyExpires ? <span className={new Date(d.itglue.warrantyExpires) < new Date() ? 'text-warning' : ''}>{d.itglue.warrantyExpires}</span> : null}</Field>
              </div>}
            </Panel>

            {d.rmm ? <HuntressDeviceCard uid={d.rmm.uid} /> : <Panel title="Huntress" icon={ShieldCheck} muted><div className="px-4 py-4 text-xs text-muted-foreground">Huntress is matched through the Datto RMM device; this one has none.</div></Panel>}
            {d.rmm && <ScreenConnectDeviceCard uid={d.rmm.uid} />}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel title={`Open alerts${d.alerts.length ? ` · ${d.alerts.length}` : ''}`} icon={AlertTriangle}>
              {!d.rmm ? <Empty>No RMM agent, so no alerts.</Empty> : d.alerts.length === 0 ? <Empty>No open alerts.</Empty> : (
                <ul className="max-h-80 divide-y divide-divider overflow-y-auto">
                  {d.alerts.map((a) => (
                    <li key={a.alertUid} className="px-4 py-2">
                      <div className="flex items-center gap-2 text-sm"><Pill tone={a.priority === 'Critical' || a.priority === 'High' ? 'bad' : a.priority === 'Moderate' ? 'warn' : 'muted'}>{a.priority}</Pill><span className="truncate text-foreground">{a.summary}</span></div>
                      <div className="text-[11px] text-muted-foreground">{a.kind} · {fmtRelative(a.timestamp)}{a.ticketNumber && ` · ${a.ticketNumber}`}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title={`Open tickets on this device${d.tickets.length ? ` · ${d.tickets.length}` : ''}`} icon={Ticket}
              actions={d.ci && <span className="text-muted-foreground">CI #{d.ci.id}</span>}>
              {!d.ci ? <Empty>No active Autotask configuration item for this device.</Empty>
                : <TicketList items={d.tickets} error={d.ticketsError} empty="No open tickets on this configuration item." />}
            </Panel>

            <Panel title="People" icon={Users}>
              {d.users.length === 0 ? <Empty>No user is tied to this device (no Intune primary user, CI contact or Azure user).</Empty> : (
                <ul className="divide-y divide-divider">
                  {d.users.map((u) => (
                    <li key={u.key} className="px-4 py-2">
                      <Link to={insightsPersonPath(u.key)} className="text-sm font-medium text-foreground hover:underline">{u.label}</Link>
                      <div className="text-[11px] text-muted-foreground">{u.key.includes('@') && u.key !== u.label ? `${u.key} · ` : ''}{u.via}</div>
                    </li>
                  ))}
                </ul>
              )}
              {d.rmm && <div className="border-t border-divider px-4 py-2 text-xs"><Link to={rmmDevicePath(d.rmm.uid)} className="text-muted-foreground hover:text-foreground hover:underline">Log a ticket from the RMM device page →</Link></div>}
            </Panel>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-4 text-xs text-muted-foreground">{children}</div>;
}
